import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { PlaylistDragProvider, PlaylistItem } from './PlaylistItem';
import { PlaylistItemManager, usePlaylistItemManager } from './PlaylistItemManager';
import { PlaylistItemSearchManager, PlaylistItemSearchProvider, usePlaylistItemSearch } from './PlaylistItemSearchManager';
import { useMemo, useRef } from 'react';
import { useCanUserEditPlaylist } from '../../savedItems/savedItemsHooks';
import { useLearningContentCardModels } from 'academy-common-lib/features/cards/hooks/useLearningContentCardModels';
import ApiError from '../../../components/Errors/ApiError';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Button } from 'foundations-components/transitional/components/Button';
import { convertToSupportedLanguage } from 'academy-common-lib/constants/academySupportedLanguages';
import { AddItemsToPlaylistModal } from './AddItemsToPlaylistModal';
import UsageTracker from '../../../utils/UsageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const PlaylistItemCount = () => {
  const {
    numberOfSelectedItems: selectedExistingPlaylistItems
  } = usePlaylistItemManager();
  const {
    numberOfSelectedItems: selectedNewPlaylistItems
  } = usePlaylistItemSearch();
  const count = selectedNewPlaylistItems > 0 ? selectedNewPlaylistItems : selectedExistingPlaylistItems;
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.savedItems.playlistDetails.numberOfPlaylistItems",
    options: {
      count
    }
  });
};
const PlaylistItemsHeader = ({
  playlistItemsCount,
  onAddToPlaylistClick
}) => {
  if (!playlistItemsCount) {
    return /*#__PURE__*/_jsx(PlaylistItemCount, {});
  }
  return /*#__PURE__*/_jsx(TFlex, {
    align: "center",
    justify: "between",
    children: /*#__PURE__*/_jsxs(List, {
      marginBlockEnd: "400",
      use: "inline-divided",
      children: [/*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(PlaylistItemCount, {})
      }), /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.savedItems.playlistDetails.dragToReorder"
        })
      }), /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(Button, {
          "data-test-id": "add-to-playlist-link-button",
          onClick: onAddToPlaylistClick,
          use: "link",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.savedItems.playlistDetails.addToPlaylist"
          })
        })
      })]
    })
  });
};
const PlaylistItemSearchManagerList = ({
  playlistId,
  playlistItems
}) => {
  const itemsForQuery = useMemo(() => {
    return (playlistItems.map(({
      itemId,
      type,
      language
    }) => ({
      id: itemId,
      type: type,
      language: language
    })) || []).sort((a, b) => a.id - b.id);
  }, [playlistItems]);

  // retrieves hydrated card models for the elements to render
  const {
    cardModels,
    cardModelsError,
    cardModelsLoading,
    userProgressDataLoading
  } = useLearningContentCardModels({
    items: itemsForQuery
  });
  if (cardModelsError) return /*#__PURE__*/_jsx(ApiError, {
    error: cardModelsError
  });
  return /*#__PURE__*/_jsx(PlaylistDragProvider, {
    children: /*#__PURE__*/_jsx(View, {
      marginBlockStart: "400",
      children: /*#__PURE__*/_jsx(List, {
        "data-test-id": "playlist-items-list",
        ordered: true,
        children: playlistItems.map((playlistItem, i) => {
          const cardModel = cardModels === null || cardModels === void 0 ? void 0 : cardModels.find(({
            id,
            type,
            language
          }) => id === playlistItem.itemId && type === playlistItem.type && convertToSupportedLanguage(language) === playlistItem.language);
          return /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(PlaylistItem, {
              cardModel: cardModel,
              isLoadingCardModel: cardModelsLoading || userProgressDataLoading,
              playlistId: playlistId,
              playlistItem: playlistItem,
              playlistLength: playlistItems.length,
              position: i
            })
          }, playlistItem.itemId);
        })
      })
    })
  });
};
export const PlaylistItems = ({
  isAddItemsModalOpen,
  playlistId,
  setIsAddItemsModalOpen
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const userCanEditPlaylist = useCanUserEditPlaylist(playlistId);
  const scrollRef = useRef(null);
  const openAddItemsModal = () => {
    setIsAddItemsModalOpen(true);
    UsageTracker.trackStandalone('Playlist Interaction', {
      action: 'open-add-to-playlist-modal'
    });
  };
  return /*#__PURE__*/_jsx(PlaylistItemManager, {
    playlistId: playlistId,
    render: playlistItems => {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("div", {
          ref: scrollRef,
          children: /*#__PURE__*/_jsx(Text, {
            as: isMobile ? 'h5' : 'h4',
            marginBlockEnd: "100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.savedItems.playlistDetails.playlistItems"
            })
          })
        }), /*#__PURE__*/_jsxs(PlaylistItemSearchProvider, {
          playlistId: playlistId,
          children: [userCanEditPlaylist && /*#__PURE__*/_jsx(PlaylistItemsHeader, {
            onAddToPlaylistClick: openAddItemsModal,
            playlistItemsCount: playlistItems.length
          }), /*#__PURE__*/_jsx(PlaylistItemSearchManager, {
            listView: /*#__PURE__*/_jsxs(_Fragment, {
              children: [playlistItems.length > 0 && /*#__PURE__*/_jsx(PlaylistItemSearchManagerList, {
                playlistId: playlistId,
                playlistItems: playlistItems
              }), userCanEditPlaylist && /*#__PURE__*/_jsx(View, {
                marginBlockStart: "600",
                children: /*#__PURE__*/_jsx(Button, {
                  "data-test-id": "add-to-playlist-primary-button",
                  onClick: openAddItemsModal,
                  use: "primary",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.savedItems.playlistDetails.addToPlaylist"
                  })
                })
              })]
            })
          })]
        }), /*#__PURE__*/_jsx(AddItemsToPlaylistModal, {
          isOpen: isAddItemsModalOpen,
          onClose: () => setIsAddItemsModalOpen(false),
          playlistId: playlistId
        })]
      });
    }
  });
};